import { useState } from 'react';
import { DateTime } from 'luxon';
import { Box, FilterOptionsState, TextField } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import match from 'autosuggest-highlight/match/index.js';
import parse from 'autosuggest-highlight/parse/index.js';

import { baseDefinedAndNotNullNarrowing, toSentenceCase } from '@initia-digital/ts-utils';
import { getFreshTokensAndClaims, useMSALPayload } from '@initia-digital/auth-client-utils';
import { IContractorMin, IHazard, IHazardClass, IPlanRegisterHazard, ITag } from '@initia-digital/types/work-and-safety';
import { IAzureAdUser } from '@initia-digital/types/identity';

import { IGenericMultiAutocompleteProps, IMultiAutocompleteProps, ISingleAutocompleteProps, ITagAutocompleteProps } from '../interfaces/componentInterfaces';
import { fetchContractorList, fetchHazardClassList, fetchHazardList, fetchMsGroup, fetchTagList } from '../api/query';
import { BasicLoader, Typography } from '@initia-digital/component-library';
import { convertHazardForPlan } from '../utils';
import { useFullTeamList, useHazardClassList, useHazardList } from '../hooks';


interface IAutoCompleteOption { label: string, value: string }


const tagFilter = createFilterOptions<ITag>();
const teamMemberMinFilter = createFilterOptions<IAzureAdUser & IAutoCompleteOption>();
const contractorMinFilter = createFilterOptions<IContractorMin & IAutoCompleteOption>();
const hazardClassFilter = createFilterOptions<IHazardClass & IAutoCompleteOption>();
const HazardFilter = createFilterOptions<IHazard & IAutoCompleteOption>();
const PlanRegisterHazardFilter = createFilterOptions<IPlanRegisterHazard & IAutoCompleteOption>();


interface ITagListQueryResult {
    tags: ITag[];
    idTokenClaims: {
        preferred_username?: string;
    };
}


export const TagAutoComplete = (props: IMultiAutocompleteProps & ITagAutocompleteProps) => {
    // -------------------------------
    const { name, handleAutoSuggestSelectStateChange, selectedValues, type } = props;
    const theme = useTheme();

    const authDetail = useMSALPayload();

    const { isLoading: isTagListLoading, data: tagList } = useQuery<ITagListQueryResult>({
        queryKey: ['tagListMin'],
        queryFn: async () => {
            // --------------------------------
            const { msal } = authDetail;
            const { idTokenClaims } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });
            const tags = await fetchTagList(authDetail, { type: 'objective' });
            return { tags, idTokenClaims };
        },
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    const tagValues = tagList?.tags || [];
    const values = baseDefinedAndNotNullNarrowing(authDetail) && !isTagListLoading && baseDefinedAndNotNullNarrowing(tagList) ? [
        ...tagValues.filter(tag => selectedValues.includes(tag.value)),
        ...selectedValues.filter(value => !baseDefinedAndNotNullNarrowing(tagValues.find(tag => tag.value === value)))
            .map(value => {
                return {
                    value: value,
                    label: value,
                    id: "",
                    type,
                    createdBy: tagList.idTokenClaims?.preferred_username?.toLowerCase(),
                    creationTimestamp: DateTime.local().toISO(),
                    updatedBy: tagList.idTokenClaims?.preferred_username?.toLowerCase(),
                    lastUpdateTimestamp: DateTime.local().toISO()
                } as ITag;
            })
    ] : [];

    if ( isTagListLoading || !baseDefinedAndNotNullNarrowing(tagList)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                {toSentenceCase(type.toLowerCase())} Tags
            </Typography>
            <Autocomplete
                size='small'
                multiple={true}
                limitTags={2}
                fullWidth={true}
                value={values}
                onChange={(_, newValue) => {
                    // (1) In the case of a scenario someone hits 'ENTER' the value will be a string (freesolo capability)
                    // In this case convert to our AutoComplete option interface
                    const updatedValue = newValue
                        .filter((selectedValue, idx) => {
                            // Check if any free solo values already exist and if so filter out of the list
                            const selectedValueForComparison = typeof selectedValue !== 'string' ? selectedValue.value : selectedValue;
                            const isDuplicate = baseDefinedAndNotNullNarrowing([...newValue.slice(idx + 1)].find(innerSelectedValue => (typeof innerSelectedValue !== 'string' ? innerSelectedValue.value : innerSelectedValue) === selectedValueForComparison));
                            return !isDuplicate;
                        })
                        .map(selectedValue => {
                            // --------------------------------------
                            if (typeof selectedValue === 'string') {
                                return selectedValue.toLowerCase();
                            }
                            else {
                                // Also here we want to Update new label if it inclues the 'Add "' prefix
                                return selectedValue.label.includes('Add "') ? selectedValue.value : selectedValue.label;
                            }
                        });

                    handleAutoSuggestSelectStateChange({ target: { value: updatedValue, name } });
                }}
                filterOptions={(options, params) => {
                    const filtered = tagFilter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option.label);

                    if (inputValue !== '' && !isExisting) {
                        filtered.push({
                            value: inputValue,
                            label: `Add "${inputValue}"`,
                            id: "",
                            type,
                            createdBy: tagList.idTokenClaims?.preferred_username?.toLowerCase(),
                            creationTimestamp: DateTime.local().toISO(),
                            updatedBy: tagList.idTokenClaims?.preferred_username?.toLowerCase(),
                            updatedTimestamp: DateTime.local().toISO()
                        });
                    }

                    return filtered;
                }}
                id={`${type.toLowerCase()}-autocomplete`}
                options={tagList.tags}
                renderOption={({ key, ...rest }, option: (ITag), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = !optionLabel.includes('Add "') ? match(optionLabel, inputValue) : [];
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                freeSolo
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} />
                )}
            />
        </Box>
    );
}


export const TeamMembersAutoComplete = (props: IMultiAutocompleteProps) => {
    // -------------------------------
    // PROPS
    const { name, handleAutoSuggestSelectStateChange, selectedValues } = props;
    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { isLoading: isTeamLoading, data: teamData } = useQuery({
        queryKey: ['fullTeamList'],
        queryFn: () => fetchMsGroup(authDetail, 'all-staff'),
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });


    const options = baseDefinedAndNotNullNarrowing(authDetail) && !isTeamLoading && baseDefinedAndNotNullNarrowing(teamData) ?
                    teamData.map(teamMember => ({ ...teamMember, label: teamMember.displayName, value: teamMember.displayName })) : [];
    const values = baseDefinedAndNotNullNarrowing(authDetail) && !isTeamLoading && baseDefinedAndNotNullNarrowing(teamData) ? [
        ...options.filter(teamMember => selectedValues.includes(teamMember.displayName)),
        ...selectedValues.filter(value => !baseDefinedAndNotNullNarrowing(teamData.find(teamMember => teamMember.displayName === value)))
            .map(value => {
                return {                    
                    label: value,
                    value: value,
                    id: "",
                    businessPhones: [],
                    displayName: value,
                    givenName: "",
                    jobTitle: null,
                    mail: "",
                    mobilePhone: null,
                    officeLocation: null,
                    preferredLanguage: '',
                    surname: "",
                    userPrincipalName: ""
                };
            })
    ] : [];

    if ( isTeamLoading || !baseDefinedAndNotNullNarrowing(teamData)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                Team Members
            </Typography>
            <Autocomplete
                multiple={true}
                limitTags={2}
                fullWidth={true}
                value={values}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                    // (1) In the case of a scenario someone hits 'ENTER' the value will be a string (freesolo capability)
                    // In this case convert to our AutoComplete option interface
                    const updatedValue = newValue
                        .filter((selectedValue, idx) => {
                            // Check if any free solo values already exist and if so filter out of the list
                            const selectedValueForComparison = typeof selectedValue !== 'string' ? selectedValue.value : selectedValue;
                            const isDuplicate = baseDefinedAndNotNullNarrowing([...newValue.slice(idx + 1)].find(innerSelectedValue => (typeof innerSelectedValue !== 'string' ? innerSelectedValue.value : innerSelectedValue) === selectedValueForComparison));
                            return !isDuplicate;
                        })
                        .map(selectedValue => {
                            // --------------------------------------
                            if (typeof selectedValue === 'string') {
                                return selectedValue;
                            }
                            else {
                                // Also here we want to Update new label if it inclues the 'Add "' prefix
                                return selectedValue.label.includes('Add "') ? selectedValue.value : selectedValue.label;
                            }
                        });

                    handleAutoSuggestSelectStateChange({ target: { value: updatedValue, name } });
                }}
                filterOptions={(options: (IAzureAdUser & IAutoCompleteOption)[], params: FilterOptionsState<(IAzureAdUser & IAutoCompleteOption)>) => {
                    const filtered = teamMemberMinFilter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option.label);

                    if (inputValue !== '' && !isExisting) {
                        filtered.push({                            
                            value: inputValue,
                            label: `add "${inputValue}"`,
                            id: "",
                            businessPhones: [],
                            displayName: "",
                            givenName: "",
                            jobTitle: null,
                            mail: "",
                            mobilePhone: null,
                            officeLocation: null,
                            preferredLanguage: '',
                            surname: "",
                            userPrincipalName: ""
                        });
                    }

                    return filtered;
                }}
                id="team-member-autocomplete"
                options={options}
                renderOption={({ key, ...rest }, option: IAzureAdUser & IAutoCompleteOption, { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = !optionLabel.includes('Add "') ? match(optionLabel, inputValue) : [];
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                freeSolo
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} />
                )}
            />
        </Box>
    );
}


export const TeamMembersMultiselectAutoComplete = (props: IMultiAutocompleteProps) => {
    // -------------------------------
    // PROPS
    const { name, handleAutoSuggestSelectStateChange, selectedValues } = props;

    // CUSTOM HOOKS
    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { fullTeamListLoading, fullTeamListData } = useFullTeamList(authDetail, true);


    const options = baseDefinedAndNotNullNarrowing(authDetail) && !fullTeamListLoading && baseDefinedAndNotNullNarrowing(fullTeamListData) ?
                    fullTeamListData.map(teamMember => ({ ...teamMember, label: teamMember.displayName, value: teamMember.displayName })) : [];
    const values = baseDefinedAndNotNullNarrowing(authDetail) && !fullTeamListLoading && baseDefinedAndNotNullNarrowing(fullTeamListData) ? [
        ...options.filter(teamMember => selectedValues.includes(teamMember.displayName)),
        ...selectedValues.filter(value => !baseDefinedAndNotNullNarrowing(fullTeamListData.find(teamMember => teamMember.displayName === value)))
            .map(value => {
                return {                    
                    label: value,
                    value: value,
                    id: "",
                    businessPhones: [],
                    displayName: value,
                    givenName: "",
                    jobTitle: null,
                    mail: "",
                    mobilePhone: null,
                    officeLocation: null,
                    preferredLanguage: '',
                    surname: "",
                    userPrincipalName: ""
                };
            })
    ] : [];

    if ( fullTeamListLoading || !baseDefinedAndNotNullNarrowing(fullTeamListData)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            {
                props.showLabel && (
                <Typography
                    fontSize={"0.8rem"}
                    fontWeight={300}
                    color={theme.palette.primary.contrastText}
                >
                    Initia Team Members
                </Typography>
                )
            }
            <Autocomplete
                size='small'
                multiple={true}
                limitTags={2}
                fullWidth={true}
                value={values}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                    // (1) In the case of a scenario someone hits 'ENTER' the value will be a string (freesolo capability)
                    // In this case convert to our AutoComplete option interface
                    const updatedValue = newValue
                        .filter((selectedValue, idx) => {
                            // Check if any free solo values already exist and if so filter out of the list
                            const selectedValueForComparison = typeof selectedValue !== 'string' ? selectedValue.value : selectedValue;
                            const isDuplicate = baseDefinedAndNotNullNarrowing([...newValue.slice(idx + 1)].find(innerSelectedValue => (typeof innerSelectedValue !== 'string' ? innerSelectedValue.value : innerSelectedValue) === selectedValueForComparison));
                            return !isDuplicate;
                        })
                        .map(selectedValue => {
                            // --------------------------------------
                            if (typeof selectedValue === 'string') {
                                return selectedValue;
                            }
                            else {
                                // Also here we want to Update new label if it inclues the 'Add "' prefix
                                return selectedValue.label.includes('Add "') ? selectedValue.value : selectedValue.label;
                            }
                        });

                    handleAutoSuggestSelectStateChange({ target: { value: updatedValue, name } });
                }}
                filterOptions={(options: (IAzureAdUser & IAutoCompleteOption)[], params: FilterOptionsState<(IAzureAdUser & IAutoCompleteOption)>) => {
                    const filtered = teamMemberMinFilter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option.label);

                    if (inputValue !== '' && !isExisting) {
                        filtered.push({
                            value: inputValue,
                            label: `Add "${inputValue}"`,
                            id: "",
                            businessPhones: [],
                            displayName: "",
                            givenName: "",
                            jobTitle: null,
                            mail: "",
                            mobilePhone: null,
                            officeLocation: null,
                            preferredLanguage: '',
                            surname: "",
                            userPrincipalName: ""
                        });
                    }

                    return filtered;
                }}
                id="team-member-autocomplete"
                options={options}
                renderOption={({ key, ...rest }, option: IAzureAdUser & IAutoCompleteOption, { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = !optionLabel.includes('Add "') ? match(optionLabel, inputValue) : [];
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                freeSolo
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} />
                )}
                disableCloseOnSelect={true}
            />
        </Box>
    );
}



export const ContractorsAutoComplete = (props: IMultiAutocompleteProps) => {
    // -------------------------------
    // PROPS
    const { name, label, handleAutoSuggestSelectStateChange, selectedValues } = props;

    let setLabel = 'Contractors and subcontractors';
    if (baseDefinedAndNotNullNarrowing(label)) {
        // --------------------------------
        setLabel = label;
    }

    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { isLoading: isContractorListLoading, data: contractorList } = useQuery({
        queryKey: ['contractorListMin'],
        queryFn: () => fetchContractorList(authDetail),
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    const values = baseDefinedAndNotNullNarrowing(authDetail) && !isContractorListLoading && baseDefinedAndNotNullNarrowing(contractorList) ? [
        ...contractorList.filter(contractor => selectedValues.includes(contractor.value)),
        ...selectedValues.filter(value => !baseDefinedAndNotNullNarrowing(contractorList?.find(contractor => contractor.value === value)))
            .map(value => {
                return {
                    value,
                    label: value
                } as (IContractorMin & IAutoCompleteOption)
            })
    ]: [];

    if ( isContractorListLoading || !baseDefinedAndNotNullNarrowing(contractorList)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                { setLabel }
            </Typography>
            <Autocomplete
                multiple={true}
                limitTags={2}
                fullWidth={true}
                value={values}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                    // (1) In the case of a scenario someone hits 'ENTER' the value will be a string (freesolo capability)
                    // In this case convert to our AutoComplete option interface
                    const updatedValue = newValue
                        .filter((selectedValue, idx) => {
                            // Check if any free solo values already exist and if so filter out of the list
                            const selectedValueForComparison = typeof selectedValue !== 'string' ? selectedValue.value : selectedValue;
                            const isDuplicate = baseDefinedAndNotNullNarrowing([...newValue.slice(idx + 1)].find(innerSelectedValue => (typeof innerSelectedValue !== 'string' ? innerSelectedValue.value : innerSelectedValue) === selectedValueForComparison));
                            return !isDuplicate;
                        })
                        .map(selectedValue => {
                            // --------------------------------------
                            if (typeof selectedValue === 'string') {
                                return selectedValue;
                            }
                            else {
                                // Also here we want to Update new label if it inclues the 'Add "' prefix
                                return selectedValue.label.includes('Add "') ? selectedValue.value : selectedValue.label;
                            }
                        });



                    handleAutoSuggestSelectStateChange({ target: { value: updatedValue, name } });
                }}
                renderTags={() => null}
                filterOptions={(options: (IContractorMin & IAutoCompleteOption)[], params: FilterOptionsState<(IContractorMin & IAutoCompleteOption)>) => {
                    const filtered = contractorMinFilter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option.label);

                    if (inputValue !== '' && !isExisting) {
                        filtered.push({
                            value: inputValue,
                            label: `Add "${inputValue}"`,
                            id: "",
                            type: "",
                            createdBy: "",
                            creationTimestamp: "",
                            updatedBy: "",
                            lastUpdateTimestamp: "",
                            contactName: "",
                            contactPhone: "",
                            contactEmail: ""
                        });
                    }

                    return filtered;
                }}
                id="contractor-autocomplete"
                options={contractorList}
                renderOption={({ key, ...rest }, option: ((IContractorMin & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = !optionLabel.includes('Add "') ? match(optionLabel, inputValue) : [];
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                freeSolo
                renderInput={(params) => (
                    <TextField
                        placeholder={`Search or add new`}
                        sx={{
                            bgcolor: 'primary.main', color: 'primary.contrastText',
                            '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                            '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                            '& .MuiOutlinedInput-root': { borderRadius: '5px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                        }}
                        {...params} 
                    />
                )}
            />
        </Box>
    );
}


export const HazardClassAutoComplete = (props: ISingleAutocompleteProps<IHazardClass>) => {
    // -------------------------------
    const { name, handleAutoSuggestSelectStateChange, value, clearOnBlur, blurOnSelect } = props;
    let shouldClearOnBlur = false;
    let shouldBlurOnSelect = false;

    if (baseDefinedAndNotNullNarrowing(clearOnBlur)) { shouldClearOnBlur = clearOnBlur; }
    if (baseDefinedAndNotNullNarrowing(blurOnSelect)) { shouldBlurOnSelect = blurOnSelect; }

    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { hazardClassListLoading, hazardClassListData } = useHazardClassList(authDetail, true);

    if ( hazardClassListLoading || !baseDefinedAndNotNullNarrowing(hazardClassListData)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                Hazard Class
            </Typography>
            <Autocomplete
                multiple={false}
                autoSelect={true}
                limitTags={2}
                fullWidth={true}
                clearOnBlur={shouldClearOnBlur}
                blurOnSelect={shouldBlurOnSelect}
                value={value}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                    // -------------------------------------------
                    let selectedValue = newValue;
                    if (typeof selectedValue === 'string') {
                        selectedValue = {
                            id: '',
                            value: selectedValue,
                            label: selectedValue,
                            createdBy: '',
                            creationTimestamp: '',
                            updatedBy: '',
                            lastUpdateTimestamp: '',
                            lastUsedBy: "",
                            lastUsedTimestamp: "",
                            lastHazardAddedId: "",
                            lastHazardAddedName: ""
                        } as IHazardClass;
                    }
                    if (baseDefinedAndNotNullNarrowing(selectedValue)) {
                        // Also here we want to Update new label if it inclues the 'Add "' prefix
                        selectedValue.label = selectedValue.label.includes('Add "') ? selectedValue.value : selectedValue.label;
                    }
                    handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                }}
                filterOptions={(options, params: FilterOptionsState<(IHazardClass & IAutoCompleteOption)>) => {
                    const filtered = hazardClassFilter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option.label);

                    if (inputValue !== '' && !isExisting) {
                        filtered.push({
                            value: inputValue,
                            label: `Add "${inputValue}"`,
                            id: "",
                            createdBy: "",
                            creationTimestamp: "",
                            updatedBy: "",
                            lastUpdateTimestamp: "",
                            lastUsedBy: "",
                            lastUsedTimestamp: "",
                            lastHazardAddedId: "",
                            lastHazardAddedName: ""
                        });
                    }

                    return filtered;
                }}
                id="hazard-class-autocomplete"
                options={hazardClassListData}
                renderOption={({ key, ...rest }, option: ((IHazardClass & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = !optionLabel.includes('Add "') ? match(optionLabel, inputValue) : [];
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                freeSolo
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px', padding: '0px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} />
                )}
            />
        </Box>
    );
}


export const ClearingHazardClassAutoComplete = (props: ISingleAutocompleteProps<IHazardClass>) => {
    // -------------------------------
    const [inputValue, setInputValue] = useState('');
    const [lastSelectedValue, setLastSelectedValue] = useState<string | null>(null);

    const { name, handleAutoSuggestSelectStateChange, value, clearOnBlur, blurOnSelect } = props;
    let shouldClearOnBlur = false;
    let shouldBlurOnSelect = false;

    if (baseDefinedAndNotNullNarrowing(clearOnBlur)) { shouldClearOnBlur = clearOnBlur; }
    if (baseDefinedAndNotNullNarrowing(blurOnSelect)) { shouldBlurOnSelect = blurOnSelect; }


    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { hazardClassListLoading, hazardClassListData } = useHazardClassList(authDetail, true);

    if ( hazardClassListLoading || !baseDefinedAndNotNullNarrowing(hazardClassListData)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                Hazard Class
            </Typography>
            <Autocomplete
                multiple={false}
                autoSelect={false}
                limitTags={2}
                fullWidth={true}
                clearOnBlur={shouldClearOnBlur}
                blurOnSelect={shouldBlurOnSelect}
                value={value}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onInputChange={(_, newValue) => {
                    // -------------------------------------------          
                    setInputValue(newValue);
                }}
                onChange={(_, newValue) => {
                    // -------------------------------------------   
                    let selectedValue = newValue;
                    if (typeof selectedValue === 'string') {
                        selectedValue = {
                            id: '',
                            value: selectedValue,
                            label: selectedValue,
                            createdBy: '',
                            creationTimestamp: '',
                            updatedBy: '',
                            lastUpdateTimestamp: '',
                            lastUsedBy: "",
                            lastUsedTimestamp: "",
                            lastHazardAddedId: "",
                            lastHazardAddedName: ""
                        } as IHazardClass;
                    }
                    if (baseDefinedAndNotNullNarrowing(selectedValue)) {
                        // Also here we want to Update new label if it inclues the 'Add "' prefix
                        selectedValue.label = selectedValue.label.includes('Add "') ? selectedValue.value : selectedValue.label;
                    }
                    if (baseDefinedAndNotNullNarrowing(selectedValue)) {
                        setLastSelectedValue(selectedValue.label);
                    }
                    setInputValue('');
                    handleAutoSuggestSelectStateChange({ target: { value: (selectedValue) ? selectedValue.label : '', name } });
                }}
                filterOptions={(options: (IHazardClass & IAutoCompleteOption)[], params: FilterOptionsState<(IHazardClass & IAutoCompleteOption)>) => {
                    const filtered = hazardClassFilter(options, params);
                    return filtered;
                }}
                id="hazard-class-autocomplete"
                options={hazardClassListData}
                renderOption={({ key, ...rest }, option: ((IHazardClass & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = !optionLabel.includes('Add "') ? match(optionLabel, inputValue) : [];
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px', padding: '0px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} inputProps={{ ...params.inputProps, value: inputValue }} />
                )}
            />
            {
                baseDefinedAndNotNullNarrowing(lastSelectedValue) && lastSelectedValue !== "" &&
                <Typography
                    fontSize={"0.8rem"}
                    fontWeight={300}
                    color={theme.palette.primary.contrastText}
                >
                    {`last selection: ${lastSelectedValue}`}
                </Typography>
            }
        </Box>
    );
}


export const HazardClassAutoCompleteMultiSelect = (props: IGenericMultiAutocompleteProps<IHazardClass>) => {
    // -------------------------------
    const [inputValue, setInputValue] = useState('');
    const { name, handleAutoSuggestSelectStateChange, selectedValues, placeholder } = props;

    let setPlaceholder = '';
    if ( baseDefinedAndNotNullNarrowing(placeholder)) {
        // ----------------------------
        setPlaceholder = placeholder;
    }

    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { hazardClassListLoading, hazardClassListData } = useHazardClassList(authDetail);

    if ( hazardClassListLoading || !baseDefinedAndNotNullNarrowing(hazardClassListData)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                Hazard Class
            </Typography>
            <Autocomplete
                inputValue={inputValue}
                multiple={true}
                limitTags={2}
                fullWidth={true}
                value={selectedValues}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onInputChange={(_, value, reason) => { if (reason !== 'reset') { setInputValue(value) } }}
                onChange={(_, newValues, details) => {
                    // -------------------------------------------
                    if (details === 'selectOption') {
                        const selectedValue = newValues.find(newValue => !baseDefinedAndNotNullNarrowing(selectedValues.find(selectedValue => selectedValue.value === newValue.value)));
                        handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                    }
                    else if (details === 'removeOption') {
                        const selectedValue = selectedValues.find(selectedValue => !baseDefinedAndNotNullNarrowing(newValues.find(newValue => newValue.value === selectedValue.value)));
                        handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                    }
                }}
                filterOptions={(options: (IHazardClass & IAutoCompleteOption)[], params: FilterOptionsState<(IHazardClass & IAutoCompleteOption)>) => {
                    const filtered = hazardClassFilter(options, params);
                    return filtered;
                }}
                id="hazard-class-autocomplete"
                options={hazardClassListData}
                renderOption={({ key, ...rest }, option: ((IHazardClass & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = !optionLabel.includes('Add "') ? match(optionLabel, inputValue) : [];
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px', padding: '0px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} inputProps={{ ...params.inputProps, value: inputValue, placeholder: setPlaceholder }} />
                )}
                renderTags={() => { /* Tags currently hidden */ return <></> }}
                disableCloseOnSelect={true}
            />
        </Box>
    );
}


export const ActiveHazardClassAutoCompleteMultiSelect = (props: IGenericMultiAutocompleteProps<IHazardClass>) => {
    // -------------------------------
    const [inputValue, setInputValue] = useState('');
    const { name, handleAutoSuggestSelectStateChange, selectedValues, placeholder } = props;

    let setPlaceholder = '';
    if ( baseDefinedAndNotNullNarrowing(placeholder)) {
        // ----------------------------
        setPlaceholder = placeholder;
    }

    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { isLoading: isHazardClassListLoading, data: hazardClassList } = useQuery({
        queryKey: ['activehazardClassList'],
        queryFn: () => fetchHazardClassList(authDetail, { status: 'ACTIVE' }),
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });

    if ( isHazardClassListLoading || !baseDefinedAndNotNullNarrowing(hazardClassList)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Autocomplete
                size="small"
                inputValue={inputValue}
                multiple={true}
                limitTags={2}
                fullWidth={true}
                value={selectedValues}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onInputChange={(_, value, reason) => { if (reason !== 'reset') { setInputValue(value) } }}
                onChange={(_, newValues, details) => {
                    // -------------------------------------------
                    if (details === 'selectOption') {
                        const selectedValue = newValues.find(newValue => !baseDefinedAndNotNullNarrowing(selectedValues.find(selectedValue => selectedValue.value === newValue.value)));
                        handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                    }
                    else if (details === 'removeOption') {
                        const selectedValue = selectedValues.find(selectedValue => !baseDefinedAndNotNullNarrowing(newValues.find(newValue => newValue.value === selectedValue.value)));
                        handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                    }
                }}
                filterOptions={(options: (IHazardClass & IAutoCompleteOption)[], params: FilterOptionsState<(IHazardClass & IAutoCompleteOption)>) => {
                    const filtered = hazardClassFilter(options, params);
                    return filtered;
                }}
                id="hazard-class-autocomplete"
                options={hazardClassList.filter(hazardClass => hazardClass.status === 'ACTIVE')}
                renderOption={({ key, ...rest }, option: ((IHazardClass & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = !optionLabel.includes('Add "') ? match(optionLabel, inputValue) : [];
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px', padding: '0px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} inputProps={{ ...params.inputProps, value: inputValue, placeholder: setPlaceholder }} />
                )}
                renderTags={() => { /* Tags currently hidden */ return <></> }}
                disableCloseOnSelect={true}
            />
        </Box>
    );
}


export const AllHazardAutoCompleteSingleSelect = (props: ISingleAutocompleteProps<IHazard>) => {
    // -------------------------------
    const { name, handleAutoSuggestSelectStateChange, value } = props;

    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { hazardListLoading, hazardListData } = useHazardList(authDetail, true);

    const options = baseDefinedAndNotNullNarrowing(authDetail) && !hazardListLoading && baseDefinedAndNotNullNarrowing(hazardListData) ? hazardListData.map(hazard => {
        return {
            ...hazard,
            label: `${hazard.hazardClassType} | ${hazard.hazardClass.label} | ${hazard.hazardName}`,
            value: hazard.id!
        }
    }) : [];
    const autocompleteValue = baseDefinedAndNotNullNarrowing(value) ? {
        ...value,
        label: `${value.hazardClassType} | ${value.hazardClass.label} | ${value.hazardName}`,
        value: value.id!
    } : null;


    if ( hazardListLoading || !baseDefinedAndNotNullNarrowing(hazardListData)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                Hazard
            </Typography>
            <Autocomplete
                multiple={false}
                limitTags={2}
                fullWidth={true}
                value={autocompleteValue}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                    // -------------------------------------------
                    const selectedValue = newValue;
                    handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                }}
                filterOptions={(options: (IHazard & IAutoCompleteOption)[], params: FilterOptionsState<(IHazard & IAutoCompleteOption)>) => {
                    const filtered = HazardFilter(options, params);
                    return filtered;
                }}
                id="hazard-single-autocomplete"
                options={options}
                renderOption={({ key, ...rest }, option: ((IHazard & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = match(optionLabel, inputValue);
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px', padding: '0px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} />
                )}
            />
        </Box>
    );
}


export const ActiveHazardAutoCompleteSingleSelect = (props: ISingleAutocompleteProps<IHazard>) => {
    // -------------------------------
    const { name, handleAutoSuggestSelectStateChange, value } = props;

    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { isLoading: isHazardListLoading, data: hazardList } = useQuery({
        queryKey: ['activeHazardList'],
        queryFn: () => fetchHazardList(authDetail, { status: 'ACTIVE' }),
        staleTime: 1000 * 60 * 60 * 24 // 24 hours
    });


    const options = baseDefinedAndNotNullNarrowing(authDetail) && !isHazardListLoading && baseDefinedAndNotNullNarrowing(hazardList) ? hazardList.map(hazard => {
        return {
            ...hazard,
            label: `${hazard.hazardClassType} | ${hazard.hazardClass.label} | ${hazard.hazardName}`,
            value: hazard.id!
        }
    }) : [];
    const autocompleteValue = baseDefinedAndNotNullNarrowing(value) ? {
        ...value,
        label: `${value.hazardClassType} | ${value.hazardClass.label} | ${value.hazardName}`,
        value: value.id!
    } : null;


    if ( isHazardListLoading || !baseDefinedAndNotNullNarrowing(hazardList)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                Hazard
            </Typography>
            <Autocomplete
                multiple={false}
                limitTags={2}
                fullWidth={true}
                value={autocompleteValue}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                    // -------------------------------------------
                    const selectedValue = newValue;
                    handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                }}
                filterOptions={(options: (IHazard & IAutoCompleteOption)[], params: FilterOptionsState<(IHazard & IAutoCompleteOption)>) => {
                    const filtered = HazardFilter(options, params);
                    return filtered;
                }}
                id="hazard-single-autocomplete"
                options={options}
                renderOption={({ key, ...rest }, option: ((IHazard & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = match(optionLabel, inputValue);
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px', padding: '0px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} />
                )}
            />
        </Box>
    );
}


export const AllHazardAutoCompleteMultiSelect = (props: IGenericMultiAutocompleteProps<IPlanRegisterHazard>) => {
    // -------------------------------
    const [inputValue, setInputValue] = useState('');
    const { name, handleAutoSuggestSelectStateChange, selectedValues, placeholder } = props;

    let setPlaceholder = '';
    if ( baseDefinedAndNotNullNarrowing(placeholder)) {
        // ----------------------------
        setPlaceholder = placeholder;
    }

    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { hazardListLoading, hazardListData } = useHazardList(authDetail, true);

    const options = baseDefinedAndNotNullNarrowing(authDetail) && !hazardListLoading && baseDefinedAndNotNullNarrowing(hazardListData) ? hazardListData.map((hazard: IHazard): IPlanRegisterHazard & IAutoCompleteOption => {
        return {
            ...convertHazardForPlan(hazard),
            label: `${hazard.hazardClassType} | ${hazard.hazardClass.label} | ${hazard.hazardName}`,
            value: hazard.id!
        }
    }) : [];

    const values = selectedValues.map((planHazard: IPlanRegisterHazard) => {
        return {
            ...planHazard,
            label: `${planHazard.hazardClassType} | ${planHazard.hazardClass.label} | ${planHazard.hazardName}`,
            value: planHazard.hazardId!
        }
    });

    if ( hazardListLoading || !baseDefinedAndNotNullNarrowing(hazardListData)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Typography
                fontSize={"0.8rem"}
                fontWeight={300}
                color={theme.palette.primary.contrastText}
            >
                Hazard
            </Typography>
            <Autocomplete
                inputValue={inputValue}
                multiple={true}
                limitTags={2}
                fullWidth={true}
                value={values}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onInputChange={(_, value, reason) => { if (reason !== 'reset') { setInputValue(value) } }}
                onChange={(_, newValue) => {
                    // -------------------------------------------
                    const selectedValue = newValue;
                    handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                }}
                filterOptions={(options: (IPlanRegisterHazard & IAutoCompleteOption)[], params: FilterOptionsState<(IPlanRegisterHazard & IAutoCompleteOption)>) => {
                    const filtered = PlanRegisterHazardFilter(options, params);
                    return filtered;
                }}
                id="hazard-single-autocomplete"
                options={options}
                renderOption={({ key, ...rest }, option: ((IPlanRegisterHazard & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = match(optionLabel, inputValue);
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField 
                    placeholder={setPlaceholder}
                    sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px', padding: '0px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} />
                )}
                renderTags={() => { /* Tags currently hidden */ return <></> }}
                disableCloseOnSelect={true}
            />
        </Box>
    );
}


export const ActiveHazardAutoCompleteMultiSelect = (props: IGenericMultiAutocompleteProps<IPlanRegisterHazard>) => {
    // -------------------------------
    const [inputValue, setInputValue] = useState('');
    const { name, handleAutoSuggestSelectStateChange, selectedValues, placeholder } = props;

    let setPlaceholder = '';
    if ( baseDefinedAndNotNullNarrowing(placeholder)) {
        // ----------------------------
        setPlaceholder = placeholder;
    }

    // CUSTOM HOOKS
    const theme = useTheme();
    const authDetail = useMSALPayload();

    const { hazardListLoading, hazardListData } = useHazardList(authDetail, true, { status: 'ACTIVE' });


    const options = baseDefinedAndNotNullNarrowing(authDetail) && !hazardListLoading && baseDefinedAndNotNullNarrowing(hazardListData) ? hazardListData.map((hazard: IHazard): IPlanRegisterHazard & IAutoCompleteOption => {
        return {
            ...convertHazardForPlan(hazard),
            label: `${hazard.hazardClass.label} | ${hazard.hazardName}`,
            value: hazard.id!
        }
    }): [];

    const values = selectedValues.map((planHazard: IPlanRegisterHazard) => {
        return {
            ...planHazard,
            label: `${planHazard.hazardClass.label} | ${planHazard.hazardName}`,
            value: planHazard.hazardId!
        }
    });

    if ( hazardListLoading || !baseDefinedAndNotNullNarrowing(hazardListData)) {
        return (
            <BasicLoader
                size={50}
            />
        )
    }

    return (
        <Box>
            <Autocomplete
                inputValue={inputValue}
                multiple={true}
                limitTags={2}
                fullWidth={true}
                value={values}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onInputChange={(_, value, reason) => { if (reason !== 'reset') { setInputValue(value) } }}
                onChange={(_, newValue) => {
                    // -------------------------------------------
                    const selectedValue = newValue;
                    handleAutoSuggestSelectStateChange({ target: { value: selectedValue, name } });
                }}
                filterOptions={(options: (IPlanRegisterHazard & IAutoCompleteOption)[], params: FilterOptionsState<(IPlanRegisterHazard & IAutoCompleteOption)>) => {
                    const filtered = PlanRegisterHazardFilter(options, params);
                    return filtered;
                }}
                id="hazard-single-autocomplete"
                options={options}
                renderOption={({ key, ...rest }, option: ((IPlanRegisterHazard & IAutoCompleteOption) | IAutoCompleteOption), { inputValue, selected }) => {
                    const optionLabel = option.label;
                    const matches = match(optionLabel, inputValue);
                    const parts = parse(optionLabel, matches);

                    return (
                        <li key={key} style={{ backgroundColor: (selected) ? theme.palette.primary.main : undefined }} {...rest}>
                            <Box>
                                {parts.map((part, index) => (
                                    <Box
                                        component="span"
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                            color: part.highlight ? theme.palette.secondary.main : theme.palette.primary.contrastText
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField 
                    placeholder={setPlaceholder}
                    sx={{
                        bgcolor: 'primary.main', color: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.main' }, '& .MuiChip-root': { bgcolor: 'secondary.main' },
                        '& .MuiChip-deleteIcon': { color: 'primary.contrastText' }, '& .MuiSvgIcon-root:hover': { color: 'primary.contrastText' },
                        '& .MuiOutlinedInput-root': { borderRadius: '5px', padding: '0px' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }
                    }} {...params} />
                )}
                renderTags={() => { /* Tags currently hidden */ return <></> }}
                disableCloseOnSelect={true}
            />
        </Box>
    );
}